import { useEffect, useState } from "react";
import { fetchAutoTaggingPlanNames } from "../services/autoTaggingService";
import type { AssessmentNameOption } from "../pages/AiPlanPage/TaggingPlan/AssessmentNameField";

export type UseAssessmentNameOptionsResult = Readonly<{
  options: readonly AssessmentNameOption[];
  loading: boolean;
  error: string | null;
}>;

export type UseAssessmentNameOptionsParams = Readonly<{
  /**
   * When false, skip the network request until the dropdown opens.
   * Default true (filter bars keep eager load).
   */
  enabled?: boolean;
  /**
   * Bump on each open so Add/Edit refetches fresh names (bypasses session cache).
   */
  reloadToken?: number;
}>;

/**
 * Loads Assessment Name dropdown options from GET `/plans/names`.
 * FreeSolo still allows typing a custom name when the list is empty.
 */
export function useAssessmentNameOptions(
  params?: UseAssessmentNameOptionsParams,
): UseAssessmentNameOptionsResult {
  const enabled = params?.enabled !== false;
  const reloadToken = params?.reloadToken ?? 0;
  const [options, setOptions] = useState<readonly AssessmentNameOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    if (!enabled) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);

    fetchAutoTaggingPlanNames({ force: reloadToken > 0 })
      .then((names) => {
        if (!mounted) return;
        setOptions(
          names.map((name) => ({
            id: name,
            name,
          })),
        );
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setOptions([]);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load assessment names.",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [enabled, reloadToken]);

  return {
    options,
    loading,
    error,
  };
}
